import type { PlaybookCriterionNode } from "@repo/app-types/entities/playbook";
import type { SampleGradingEntry } from "@repo/app-types/entities/playbookProposalDetail";
import { Size } from "@repo/ui/helpers/helpers";
import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "@repo/ui/tables/entityTable";
import { type FC, useMemo } from "react";
import { renderGradingValue } from "../../features/teammatePerformance/gradingHelpers.js";
import { GradingsTableContactCell } from "../../features/teammatePerformance/gradingsTableContactCell.js";
import { TeammateNameStack } from "../../features/teammatePerformance/teammateNameStack.js";

interface SampleGradingsTableProps {
  gradingValueType: PlaybookCriterionNode["grading_value_type"];
  entries: readonly SampleGradingEntry[];
  isLoading?: boolean;
  isError?: boolean;
}

// EntityTable requires entities to have an `id` field; derive it from the grading row id.
type SampleGradingRow = SampleGradingEntry & { id: string };

export const SampleGradingsTable: FC<SampleGradingsTableProps> = (props) => {
  const { gradingValueType, entries } = props;

  const rows = useMemo(
    (): SampleGradingRow[] => entries.map((e) => ({ ...e, id: e.grading.id })),
    [entries],
  );

  const columns = useMemo(
    (): EntityTableColumnDefinition<SampleGradingRow>[] => [
      {
        id: "customer",
        className: "w-[28%]",
        header: "customer",
        isRowHeader: true,
        renderCell: (entry) => (
          <GradingsTableContactCell grading={entry.grading} />
        ),
      },
      {
        id: "teammate",
        className: "w-[20%]",
        header: "Teammate",
        renderCell: (entry) => <TeammateNameStack teammate={entry.teammate} />,
      },
      {
        id: "currentGrade",
        className: "w-0",
        header: "Current grade",
        renderCell: (entry) =>
          renderGradingValue(
            gradingValueType,
            entry.baseline_grade?.value ?? undefined,
          ),
      },
      {
        id: "testGrade",
        className: "w-0",
        header: "Test grade",
        renderCell: (entry) =>
          renderGradingValue(
            gradingValueType,
            entry.tested_grade?.value ?? undefined,
          ),
      },
      {
        id: "testCommentary",
        className: "w-[50%]",
        header: "Test commentary",
        renderCell: (entry) => (
          <div>{entry.tested_grade?.commentary ?? "—"}</div>
        ),
      },
    ],
    [gradingValueType],
  );

  return (
    <EntityTable
      aria-label="Graded calls"
      columns={columns}
      entities={rows}
      isLoading={props.isLoading}
      isError={props.isError}
      emptyMessage="No graded calls in this sample."
      size={Size.SMALL}
    />
  );
};
